// 8-step sequencer driven by the lookahead scheduler.
//
// Each step carries an on/off, a pitch offset (semitones from the base note), a
// bipolar filter offset and a gate length. On each scheduled step the sequencer
// asks the host to play a note and to apply that step's filter offset, both at
// the precise audio time. Timing math is pure + unit-tested.
//
// Decoupled from the engine via `SequencerHost` so it can be tested with a fake.

import { LookaheadScheduler, type SchedulerOptions } from '../lib/scheduler';
import { NUM_STEPS, type SequencerState, type StepState, defaultStep } from './presets';

export interface SequencerHost {
  now(): number;
  noteOnAt(midi: number, when: number): void;
  noteOffAt(midi: number, when: number): void;
  /** Apply a per-step bipolar filter offset (-1..1) at `when`. */
  setSeqFilterOffset(norm: number, when: number): void;
}

// One step = an eighth note, so 8 steps = one 4/4 bar.
export function baseStepDuration(bpm: number): number {
  return 30 / Math.max(1, bpm);
}

/**
 * Duration of the step at `index` including swing. Swing lengthens on-beats
 * (even indices) and shortens off-beats (odd), preserving overall tempo.
 * `swing` in [0, ~0.75].
 */
export function stepInterval(index: number, bpm: number, swing: number): number {
  const base = baseStepDuration(bpm);
  const s = Math.max(0, Math.min(0.9, swing));
  return index % 2 === 0 ? base * (1 + s) : base * (1 - s);
}

/** Absolute start times of `count` steps beginning at `startTime`. */
export function stepStartTimes(
  startTime: number,
  count: number,
  bpm: number,
  swing: number,
): number[] {
  const times: number[] = [];
  let t = startTime;
  for (let i = 0; i < count; i++) {
    times.push(t);
    t += stepInterval(i, bpm, swing);
  }
  return times;
}

export class Sequencer {
  private readonly host: SequencerHost;
  private readonly scheduler: LookaheadScheduler;

  private steps: StepState[] = Array.from({ length: NUM_STEPS }, defaultStep);
  private bpm = 120;
  private swing = 0;
  private stepCount = NUM_STEPS;
  private baseNote = 48; // C3

  private nextStepTime = 0;
  private currentStep = 0;
  private playing = false;

  /** Fired when a step is scheduled: (stepIndex, audioTime). */
  onStep: ((index: number, when: number) => void) | null = null;

  constructor(host: SequencerHost, opts?: SchedulerOptions) {
    this.host = host;
    this.scheduler = new LookaheadScheduler(() => host.now(), opts);
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  setState(state: SequencerState): void {
    this.steps = state.steps.map((s) => ({ ...s }));
  }

  getState(): SequencerState {
    return { steps: this.steps.map((s) => ({ ...s })) };
  }

  setStep(index: number, patch: Partial<StepState>): void {
    const step = this.steps[index];
    if (step) this.steps[index] = { ...step, ...patch };
  }

  setBpm(bpm: number): void {
    this.bpm = bpm;
  }

  setSwing(swing: number): void {
    this.swing = swing;
  }

  setStepCount(count: number): void {
    this.stepCount = Math.max(1, Math.min(NUM_STEPS, Math.round(count)));
  }

  setBaseNote(midi: number): void {
    this.baseNote = midi;
  }

  start(): void {
    if (this.playing) return;
    this.playing = true;
    this.currentStep = 0;
    this.nextStepTime = this.host.now() + 0.06;
    this.scheduler.start((horizon) => this.schedule(horizon));
  }

  stop(): void {
    this.playing = false;
    this.scheduler.stop();
  }

  reset(): void {
    this.currentStep = 0;
    this.nextStepTime = this.host.now() + 0.06;
  }

  /**
   * Schedule every step whose start time falls before `horizon`. Public so it
   * can be driven directly in tests (the real scheduler calls it on a timer).
   */
  schedule(horizon: number): void {
    if (!this.playing) return;
    while (this.nextStepTime < horizon) {
      this.fireStep(this.currentStep, this.nextStepTime);
      this.nextStepTime += stepInterval(this.currentStep, this.bpm, this.swing);
      this.currentStep = (this.currentStep + 1) % this.stepCount;
    }
  }

  /** Prime timing without starting the real timer (test seam). */
  primeForTest(startTime: number): void {
    this.playing = true;
    this.currentStep = 0;
    this.nextStepTime = startTime;
  }

  private fireStep(index: number, when: number): void {
    const step = this.steps[index]!;
    // Apply the step's filter offset whether or not it triggers a note.
    this.host.setSeqFilterOffset(step.filterOffset, when);
    if (step.on) {
      const midi = this.baseNote + step.pitchOffset;
      const dur = stepInterval(index, this.bpm, this.swing) * step.gateLength;
      this.host.noteOnAt(midi, when);
      this.host.noteOffAt(midi, when + dur);
    }
    this.onStep?.(index, when);
  }
}
